<template>
    <div class="modal-content" v-if='societesLoaded'>
        <ReglePastille cle="dashboard.sondageSatisfactionConditions" />
        <img src="@/assets/img/debutEnqueteSatisfactionModal.png" alt="personne satisfaction"/>
        <p v-if='societes.length === 1' class="pre-line">{{$t('satisfaction.modal.part1', {libelleSociete: this.societeList})}}</p>
        <p  v-else class="pre-line">{{$t('satisfaction.modal.multisociete', {libelleSociete: this.societeList})}}</p>
        <div class="modal-buttons" :class="{ centered: hideLaterButton }">
            <button v-if="!hideLaterButton" @click="handleLaterClick" class='btn-later'>{{ $t('satisfaction.modal.btn.rptard') }}</button>
            <ReglePastille v-if="!hideLaterButton" cle="dashboard.sondageSatisfactionRelance" />
            <button @click="emitAnswer" class='btn-repondre'>{{ $t('satisfaction.modal.btn.rp') }}</button>
        </div>
        <!-- Boutons radio conditionnels -->
        <div v-if="showRadioOptions" class="modal-radio-options">
            <label v-for="(option, index) in radioOptions" :key="index">
                <input type="radio" v-model="responseOption" :value="option.value" @change="handleRelanceSelection"/>
                {{ libelleRadio(option) }}
            </label>
        </div>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import SatisfactionService from '@/modules/Satisfaction/services/SatisfactionService';
import dayjs from 'dayjs';
import { getRadioOptions, RadioOption } from '@/components/ui-components/radioOptions';
import StringUtils from '@/common/utils/StringUtils';
import AccountService from '@/modules/Account/services/AccountService';
import { SATISFACTION } from '@/modules/Satisfaction/store/mutation-types';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    name: 'EnqueteModalContent',
    components: { ReglePastille },
    emits: ['close', 'answer'],
    data() {
        return {
            societes: [],
            societeList: '',
            societesLoaded: false,
        };
    },
    setup(props, {emit}) {
        const clickCount = ref(0);
        const showRadioOptions = ref(false);
        const hideLaterButton = ref(false);
        const responseOption = ref('');
        const store = useStore();
        const router = useRouter();

        const compteId = computed(() => store.getters.getUserInformations.t_compte_id);
        const radioOptions = ref<RadioOption[]>([]);
        // Libellé traduit
        const libelleRadio = (option: RadioOption): string => StringUtils.traduireOuDefaut(option.cle, option.value);

        // Fonction pour v�rifier si nous sommes le 1er janvier de l'ann�e suivante et reset clickCount
        const checkNewYearReset = () => {
            const today = dayjs();
            const nextYearStart = dayjs().add(1, 'year').startOf('year');
            const isNewYear = today.isSame(nextYearStart, 'day');
            if (isNewYear) {
                clickCount.value = 0;
            }
        };

        // Fonction pour v�rifier et afficher les boutons radio
        const checkClickCount = () => {
            if (clickCount.value >= 2) {
                showRadioOptions.value = true;
                radioOptions.value = getRadioOptions('relance');
            }
            // Si le nombre de clics est >= 3, masquer le bouton "R�pondre plus tard"
            if (clickCount.value >= 3) {
                hideLaterButton.value = true;
            }
        };

        // Fonction pour r�cup�rer clickCount
        const fetchClickCount = async () => {
            try {
                const response = await SatisfactionService.getClickCount(compteId.value);
                clickCount.value = response.data;

                // V�rifiez si clickCount est � remettre � 0
                checkNewYearReset();

                // Si le nombre de clics est >= 2, afficher les boutons radio
                checkClickCount();
            } catch (error) {
                console.error('Erreur lors de la r�cup�ration du nombre de clics', error);
            }
        };

        // Fonction pour g�rer le clic sur "R�pondre plus tard" => clickCount + dateRepondrePlusTard
        const handleLaterClick = async () => {
            clickCount.value++;

            const dateRepondrePlusTard = dayjs().format('YYYY-MM-DDTHH:mm:ss');

            try {
                await SatisfactionService.plusTardClickCount({
                    clickCount: clickCount.value,
                    compteId: compteId.value,
                    dateRepondrePlusTard: dateRepondrePlusTard
                });

                checkClickCount();
                if (clickCount.value <= 2) {
                    router.push('/dashboard');
                }
            } catch (error) {
                console.error('Erreur lors de l\'envoi des donn�es', error);
            }

            emit('close');
        };

        // Fonction pour g�rer la s�lection des options radio
        const handleRelanceSelection = async () => {
            let option = '';
            let dateRepondrePlusTard = null;
            let dateNePlusAfficher = null;

            if (responseOption.value === 'Me relancer dans 3 mois') {
                option = 'me_relancer_dans_3_mois';
                dateRepondrePlusTard = dayjs().add(3, 'month').format('YYYY-MM-DDTHH:mm:ss');
            } else if (responseOption.value === 'Ne plus demander') {
                option = 'ne_plus_demander';
                dateNePlusAfficher = dayjs().add(1, 'year').startOf('year').format('YYYY-MM-DDTHH:mm:ss');
            }

            try {
                await SatisfactionService.modalGestionOptions({
                    compteId: compteId.value,
                    dateRepondrePlusTard: dateRepondrePlusTard,
                    dateNePlusAfficher: dateNePlusAfficher,
                    option: option
                });
                emit('close');
                router.push('/dashboard');
            } catch (error) {
                console.error('Erreur lors de la mise � jour des options du modal', error);
            }
        };

        onMounted(() => {
            fetchClickCount();
        });

        const emitAnswer = () => {
            emit('answer');
        };

        return {
            handleLaterClick,
            emitAnswer,
            handleRelanceSelection,
            showRadioOptions,
            hideLaterButton,
            responseOption,
            radioOptions,
            libelleRadio
        };
    },
    beforeMount() {
        this.getSocietes();
    },
    methods: {
        getSocietes() {
            AccountService.getSocieteIds(true).then((societes: any) => {
                this.societes = societes.data;
                this.societes.forEach((societe: { libelle: string }, index) => {
                    this.societeList += societe.libelle;
                    if (this.societes.length - 1 !== index) {
                        this.societeList += ', ';
                    }
                });
                this.societesLoaded = true;
                this.$store.commit(`satisfactionState/${SATISFACTION.SET_SOCIETES}`, this.societes);
            });
        },
    }
});
</script>

<style scoped>
.pre-line{
    white-space: pre-line;
}
.modal-content {
    background: white;
    padding: 20px;
    border-radius: 10px;
    text-align: center;
    max-width: 400px;
    width: 100%;
}

.modal-content img {
    max-width: 100%;
    height: auto;
    margin-bottom: 20px;
}
p {
    white-space: pre-line;
}
.modal-buttons {
    display: flex;
    justify-content: space-between;
    margin: 10px 0 10px 0;
}
.btn-later {
    background-color: var(--button-color-disabled);
}
.btn-repondre {
    background-color: #28a745;
}
.modal-buttons button {
    padding: 10px 20px;
    border: none;
    color: white;
    cursor: pointer;
    border-radius: 5px;
}

.modal-buttons.centered {
    justify-content: center;
}

.modal-radio-options {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
}

.modal-radio-options label {
    margin-right: 10px;
}
</style>

